import type { WaitlistExperienceLevel } from '@prepzio/shared';
import type { KeyboardEvent } from 'react';

import { PixelButton } from './PixelButton';

const MAX_AI_TOOLS = 20;
const MAX_MAIN_PAIN = 1000;

const EXPERIENCE_LEVELS: { value: WaitlistExperienceLevel; label: string }[] = [
  { value: 'student', label: 'Student' },
  { value: 'bootcamp', label: 'Bootcamp grad' },
  { value: 'self-taught', label: 'Self-taught' },
  { value: 'junior', label: 'Junior (0–2 yrs)' },
  { value: 'mid', label: 'Mid-level' },
  { value: 'senior', label: 'Senior' },
  { value: 'other', label: 'Other' },
];

const PRESET_AI_TOOLS = [
  'ChatGPT',
  'GitHub Copilot',
  'Cursor',
  'Claude',
  'Gemini',
  'Codeium',
] as const;

type Props = {
  experienceId: string;
  mainPainId: string;
  mainPainCounterId: string;
  customToolId: string;
  experienceLevel: WaitlistExperienceLevel | '';
  onExperienceLevelChange: (value: WaitlistExperienceLevel | '') => void;
  selectedPresets: Set<string>;
  customTools: string[];
  customToolInput: string;
  onCustomToolInputChange: (value: string) => void;
  showOtherInput: boolean;
  onToggleOtherInput: () => void;
  onTogglePreset: (tool: string) => void;
  onAddCustomTool: () => void;
  onRemoveCustomTool: (tool: string) => void;
  mainPain: string;
  onMainPainChange: (value: string) => void;
  totalAiTools: number;
  disabled?: boolean;
};

export const WaitlistProfileFields = ({
  experienceId,
  mainPainId,
  mainPainCounterId,
  customToolId,
  experienceLevel,
  onExperienceLevelChange,
  selectedPresets,
  customTools,
  customToolInput,
  onCustomToolInputChange,
  showOtherInput,
  onToggleOtherInput,
  onTogglePreset,
  onAddCustomTool,
  onRemoveCustomTool,
  mainPain,
  onMainPainChange,
  totalAiTools,
  disabled = false,
}: Props) => {
  const handleCustomToolKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onAddCustomTool();
    }
  };

  return (
    <div className="waitlist__fieldset">
      <label className="waitlist__label" htmlFor={experienceId}>
        Experience level
      </label>
      <select
        id={experienceId}
        className="waitlist__select"
        name="experienceLevel"
        value={experienceLevel}
        onChange={(e) => onExperienceLevelChange(e.target.value as WaitlistExperienceLevel | '')}
        disabled={disabled}
      >
        <option value="">Select…</option>
        {EXPERIENCE_LEVELS.map(({ value, label }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>

      <span className="waitlist__label" id={`${experienceId}-tools-label`}>
        AI tools you use
      </span>
      <div className="waitlist__chips" role="group" aria-labelledby={`${experienceId}-tools-label`}>
        {PRESET_AI_TOOLS.map((tool) => {
          const selected = selectedPresets.has(tool);
          return (
            <button
              key={tool}
              type="button"
              className={`waitlist__chip${selected ? ' waitlist__chip--on' : ''}`}
              aria-pressed={selected}
              disabled={disabled || (!selected && totalAiTools >= MAX_AI_TOOLS)}
              onClick={() => onTogglePreset(tool)}
            >
              {tool}
            </button>
          );
        })}
        <button
          type="button"
          className={`waitlist__chip${showOtherInput ? ' waitlist__chip--on' : ''}`}
          aria-pressed={showOtherInput}
          disabled={disabled}
          onClick={onToggleOtherInput}
        >
          Other
        </button>
      </div>

      {showOtherInput && !disabled && (
        <div className="waitlist__custom-tool">
          <label className="sr-only" htmlFor={customToolId}>
            Other AI tool name
          </label>
          <input
            id={customToolId}
            className="waitlist__input waitlist__input--sm"
            type="text"
            value={customToolInput}
            onChange={(e) => onCustomToolInputChange(e.target.value)}
            onKeyDown={handleCustomToolKeyDown}
            placeholder="Tool name"
            maxLength={80}
            disabled={totalAiTools >= MAX_AI_TOOLS}
          />
          <PixelButton
            type="button"
            size="sm"
            variant="ghost"
            disabled={!customToolInput.trim() || totalAiTools >= MAX_AI_TOOLS}
            onClick={onAddCustomTool}
          >
            Add
          </PixelButton>
        </div>
      )}

      {customTools.length > 0 && (
        <div className="waitlist__chips" aria-label="Custom AI tools">
          {customTools.map((tool) => (
            <button
              key={tool}
              type="button"
              className="waitlist__chip waitlist__chip--on waitlist__chip--removable"
              aria-label={`Remove ${tool}`}
              disabled={disabled}
              onClick={() => onRemoveCustomTool(tool)}
            >
              {tool} ×
            </button>
          ))}
        </div>
      )}

      <label className="waitlist__label" htmlFor={mainPainId}>
        What do you struggle with most?
      </label>
      <textarea
        id={mainPainId}
        className="waitlist__textarea"
        name="mainPain"
        value={mainPain}
        onChange={(e) => onMainPainChange(e.target.value)}
        placeholder="e.g. explaining my code in interviews, debugging without paste…"
        maxLength={MAX_MAIN_PAIN}
        rows={3}
        aria-describedby={mainPainCounterId}
        disabled={disabled}
      />
      <span id={mainPainCounterId} className="waitlist__counter" aria-live="polite">
        {mainPain.length}/{MAX_MAIN_PAIN}
      </span>
    </div>
  );
};
